import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import Header from './Header'
import Footer from './Footer'
import { useLocation } from 'react-router-dom'
import API from '../../../services/API'

// The question text is the key each answer is stored under
// (EmployeeApplication.referenceN.response) and is what Personnel see in
// admin/References.jsx — rewording a key orphans it from older responses.
const DECLARATION =
  'I confirm the information I have given is accurate to the best of my knowledge'

const SECTIONS = [
  {
    title: 'About you',
    description:
      'We have filled in what the applicant told us. Please correct anything that is out of date.',
    fields: [
      { key: 'Your Name', prefill: 'fullName', autoComplete: 'name', half: true },
      {
        key: 'Your Position/Job Title',
        label: 'Your position / job title',
        prefill: 'position',
        autoComplete: 'organization-title',
        half: true,
      },
      {
        key: 'Your Organisation/Company',
        label: 'Your organisation / company',
        prefill: 'company',
        autoComplete: 'organization',
      },
      {
        key: 'Your Email',
        prefill: 'email',
        type: 'email',
        autoComplete: 'email',
        half: true,
      },
      {
        key: 'Your Phone Number',
        prefill: 'phoneNumber',
        type: 'tel',
        autoComplete: 'tel',
        half: true,
      },
    ],
  },
  {
    title: 'Your relationship with the applicant',
    fields: [
      {
        key: 'How long have you known the applicant and in what capacity?',
        kind: 'long',
        placeholder: 'e.g. Line manager for 3 years at …',
      },
    ],
  },
  {
    title: 'About the applicant',
    fields: [
      {
        key: "Can you describe the applicant's professional skills and abilities relevant to the role they are applying for?",
        kind: 'long',
      },
      {
        key: "How would you rate the applicant's reliability and dependability?",
        kind: 'choice',
        options: ['Excellent', 'Good', 'Satisfactory', 'Poor'],
        detailLabel: 'Anything you would like to add (optional)',
      },
      {
        key: "Can you provide examples of the applicant's ability to work well within a team or independently?",
        kind: 'long',
      },
      {
        key: 'How does the applicant handle challenging situations or high-pressure environments?',
        kind: 'long',
      },
      {
        key: "Have you ever had any concerns about the applicant's conduct or professionalism?",
        kind: 'choice',
        options: ['No', 'Yes'],
        detailRequiredOn: 'Yes',
        detailLabel: 'Please give details',
      },
    ],
  },
  {
    title: 'Your recommendation',
    fields: [
      {
        key: 'Do you have any reservations or concerns about recommending the applicant for this role?',
        kind: 'choice',
        options: ['No', 'Yes'],
        detailRequiredOn: 'Yes',
        detailLabel: 'Please give details',
      },
      {
        key: 'Is there anything else you would like to share about the applicant that you believe would be helpful for us to know?',
        kind: 'long',
        optional: true,
      },
      {
        key: 'May we contact you for further clarification or additional information, if needed?',
        kind: 'choice',
        options: ['Yes', 'No'],
      },
    ],
  },
]

const ALL_FIELDS = SECTIONS.flatMap((s) => s.fields)
const detailKey = (key) => `${key}::detail`
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const fieldError = (field, values) => {
  const value = (values[field.key] ?? '').trim()
  if (!value) return field.optional ? null : 'Required'
  if (field.type === 'email' && !EMAIL_PATTERN.test(value)) {
    return 'Enter a valid email address'
  }
  if (
    field.detailRequiredOn &&
    value === field.detailRequiredOn &&
    !(values[detailKey(field.key)] ?? '').trim()
  ) {
    return 'Please give details'
  }
  return null
}

// Flattened back to the { question: answer } strings Personnel already read,
// in question order.
const buildResponse = (values) => {
  const response = {}
  for (const field of ALL_FIELDS) {
    const value = (values[field.key] ?? '').trim()
    if (!value) continue
    const detail = (values[detailKey(field.key)] ?? '').trim()
    response[field.key] = detail ? `${value} — ${detail}` : value
  }
  response.Declaration = DECLARATION
  return response
}

const draftStorageKey = (token) => `referenceDraft:${(token ?? '').slice(-32)}`
const readDraft = (token) => {
  try {
    return JSON.parse(localStorage.getItem(draftStorageKey(token))) ?? {}
  } catch {
    return {}
  }
}
const writeDraft = (token, values) => {
  try {
    localStorage.setItem(draftStorageKey(token), JSON.stringify(values))
  } catch {
    // Private mode / storage blocked — the draft is a convenience only
  }
}
const clearDraft = (token) => {
  try {
    localStorage.removeItem(draftStorageKey(token))
  } catch {
    // ignore
  }
}

const StatusCard = ({ icon: Icon, color, title, children }) => (
  <Paper variant="outlined" sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
    <Icon sx={{ fontSize: 56, color }} />
    <Typography variant="h5" fontWeight={600} mt={1.5}>
      {title}
    </Typography>
    <Typography color="text.secondary" mt={1}>
      {children}
    </Typography>
  </Paper>
)

const Question = ({ field, values, setValue, showErrors, inputRef }) => {
  const error = showErrors ? fieldError(field, values) : null
  const label = field.label ?? field.key
  const value = values[field.key] ?? ''

  if (field.kind === 'choice') {
    const showDetail =
      value &&
      (field.detailRequiredOn ? value === field.detailRequiredOn : true) &&
      field.detailLabel
    return (
      <Stack gap={1} ref={inputRef} tabIndex={-1} sx={{ outline: 'none' }}>
        <Typography fontWeight={500}>
          {label}
          {!field.optional && (
            <Box component="span" color="error.main" aria-hidden>
              {' '}
              *
            </Box>
          )}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={value || null}
          onChange={(_, next) => next !== null && setValue(field.key, next)}
          aria-label={label}
          sx={{ flexWrap: 'wrap' }}
        >
          {field.options.map((option) => (
            <ToggleButton
              key={option}
              value={option}
              sx={{ px: 2.5, textTransform: 'none', fontWeight: 500 }}
            >
              {option}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {showDetail && (
          <TextField
            label={field.detailLabel}
            multiline
            minRows={2}
            fullWidth
            value={values[detailKey(field.key)] ?? ''}
            onChange={(e) => setValue(detailKey(field.key), e.target.value)}
            error={Boolean(error) && error === 'Please give details'}
          />
        )}
        {error && <FormHelperText error>{error}</FormHelperText>}
      </Stack>
    )
  }

  const long = field.kind === 'long'
  return (
    <Box>
      {long && (
        <Typography fontWeight={500} mb={1}>
          {label}
          {!field.optional && (
            <Box component="span" color="error.main" aria-hidden>
              {' '}
              *
            </Box>
          )}
        </Typography>
      )}
      <TextField
        inputRef={inputRef}
        label={long ? undefined : label}
        placeholder={field.placeholder}
        required={!long && !field.optional}
        inputProps={long ? { 'aria-label': label } : undefined}
        type={field.type ?? 'text'}
        autoComplete={field.autoComplete ?? 'off'}
        multiline={long}
        minRows={long ? 3 : undefined}
        fullWidth
        value={value}
        onChange={(e) => setValue(field.key, e.target.value)}
        error={Boolean(error)}
        helperText={error ?? (field.optional && long ? 'Optional' : ' ')}
      />
    </Box>
  )
}

const ReferenceRequest = () => {
  const { search } = useLocation()
  const token = useMemo(() => new URLSearchParams(search).get('token'), [search])

  // loading | form | submitted | alreadyResponded | notRequired | invalid | loadError
  const [status, setStatus] = useState('loading')
  const [data, setData] = useState({})
  const [values, setValues] = useState({})
  const [declared, setDeclared] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const fieldRefs = useRef({})

  const statusFromError = (res) => {
    const code = res?.data?.errorCode
    if (code === 'alreadyResponded') return 'alreadyResponded'
    if (code === 'referenceNotRequired') return 'notRequired'
    if (code === 'tokenExpired') return 'invalid'
    return null
  }

  const load = () => {
    setStatus('loading')
    API.get('/employee/applications/reference-request-data', {
      params: { token },
    })
      .then((res) => {
        if (res.status === 200) {
          const prefill = {}
          for (const field of ALL_FIELDS) {
            if (field.prefill && res.data[field.prefill]) {
              prefill[field.key] = res.data[field.prefill]
            }
          }
          setData(res.data)
          setValues({ ...prefill, ...readDraft(token) })
          setStatus('form')
        } else {
          setData(res.data ?? {})
          setStatus(statusFromError(res) ?? 'loadError')
        }
      })
      .catch(() => setStatus('loadError'))
  }

  useEffect(load, [token])

  useEffect(() => {
    if (status === 'form') writeDraft(token, values)
  }, [values, status, token])

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }))

  const requiredFields = ALL_FIELDS.filter((f) => !f.optional)
  const completeCount = requiredFields.filter(
    (f) => !fieldError(f, values),
  ).length
  const progress = Math.round(
    ((completeCount + (declared ? 1 : 0)) / (requiredFields.length + 1)) * 100,
  )

  const handleSubmit = (e) => {
    e.preventDefault()
    setSubmitError(null)
    const firstInvalid = ALL_FIELDS.find((f) => fieldError(f, values))
    if (firstInvalid || !declared) {
      setShowErrors(true)
      const target = firstInvalid
        ? fieldRefs.current[firstInvalid.key]
        : fieldRefs.current.declaration
      target?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
      target?.focus?.({ preventScroll: true })
      return
    }
    setSubmitting(true)
    API.post(
      '/employee/applications/reference-submit',
      buildResponse(values),
      { params: { token } },
    )
      .then((res) => {
        if (res.status === 200) {
          clearDraft(token)
          setStatus('submitted')
          window.scrollTo({ top: 0, behavior: 'smooth' })
          return
        }
        const next = statusFromError(res)
        if (next) {
          setStatus(next)
        } else {
          setSubmitError(
            'We could not submit your reference. Your answers are saved on this device — please try again.',
          )
        }
      })
      .catch(() =>
        setSubmitError(
          'We could not reach the server. Your answers are saved on this device — please try again.',
        ),
      )
      .finally(() => setSubmitting(false))
  }

  const renderBody = () => {
    switch (status) {
      case 'loading':
        return (
          <Stack alignItems="center" py={10}>
            <CircularProgress />
          </Stack>
        )
      case 'submitted':
        return (
          <StatusCard
            icon={CheckCircleOutlineIcon}
            color="success.main"
            title="Reference submitted"
          >
            Thank you for taking the time to provide a reference
            {data.referenceFor ? ` for ${data.referenceFor}` : ''}. There is
            nothing further you need to do — you can close this page.
          </StatusCard>
        )
      case 'alreadyResponded':
        return (
          <StatusCard
            icon={CheckCircleOutlineIcon}
            color="success.main"
            title="Already completed"
          >
            This reference has already been submitted. Thank you — there is
            nothing further you need to do.
          </StatusCard>
        )
      case 'notRequired':
        return (
          <StatusCard
            icon={InfoOutlinedIcon}
            color="info.main"
            title="Reference no longer required"
          >
            Thank you for your time — there is nothing further you need to do.
          </StatusCard>
        )
      case 'invalid':
        return (
          <StatusCard
            icon={LinkOffIcon}
            color="text.secondary"
            title="This link is no longer valid"
          >
            It may have expired. If you still need to give a reference, please
            contact the organisation that asked you for one.
          </StatusCard>
        )
      case 'loadError':
        return (
          <StatusCard
            icon={ErrorOutlineIcon}
            color="error.main"
            title="Something went wrong"
          >
            We could not load this reference request.
            <Box mt={2}>
              <Button variant="contained" onClick={load}>
                Try again
              </Button>
            </Box>
          </StatusCard>
        )
      default:
        return renderForm()
    }
  }

  const renderForm = () => (
    <Stack component="form" noValidate onSubmit={handleSubmit} gap={2.5}>
      <Box>
        <Typography variant="overline" color="text.secondary">
          Reference request
        </Typography>
        <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.6rem', sm: '2.1rem' } }}>
          {data.referenceFor}
        </Typography>
        {data.roleAppliedFor && (
          <Typography color="text.secondary">
            Applying for the role of {data.roleAppliedFor}
          </Typography>
        )}
        <Typography mt={2}>
          Thank you for agreeing to act as a referee. Your answers help us make
          sure everyone who joins us is safe to provide care. It usually takes
          about 10 minutes.
        </Typography>
        <Stack
          direction="row"
          gap={1}
          alignItems="flex-start"
          mt={1.5}
          color="text.secondary"
        >
          <LockOutlinedIcon fontSize="small" sx={{ mt: '2px' }} />
          <Typography variant="body2">
            Your reference is seen only by the recruitment team and is not
            shared with the applicant. Answers are saved on this device as you
            type, so you can come back to finish.
          </Typography>
        </Stack>
      </Box>

      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.default',
          py: 1,
        }}
      >
        <Stack direction="row" justifyContent="space-between" mb={0.5}>
          <Typography variant="caption" color="text.secondary">
            Progress
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {progress}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Box>

      {SECTIONS.map((section, index) => (
        <Paper
          key={section.title}
          variant="outlined"
          sx={{ p: { xs: 2, sm: 3 } }}
          component="section"
        >
          <Stack direction="row" gap={1.5} alignItems="center" mb={2}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 700,
                fontSize: '0.85rem',
                flexShrink: 0,
              }}
            >
              {index + 1}
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} lineHeight={1.3}>
                {section.title}
              </Typography>
              {section.description && (
                <Typography variant="body2" color="text.secondary">
                  {section.description}
                </Typography>
              )}
            </Box>
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              columnGap: 2,
              rowGap: section.fields[0].kind ? 3 : 1,
            }}
          >
            {section.fields.map((field) => (
              <Box
                key={field.key}
                sx={{
                  gridColumn: {
                    xs: '1 / -1',
                    sm: field.half ? 'auto' : '1 / -1',
                  },
                }}
              >
                <Question
                  field={field}
                  values={values}
                  setValue={setValue}
                  showErrors={showErrors}
                  inputRef={(el) => (fieldRefs.current[field.key] = el)}
                />
              </Box>
            ))}
          </Box>
        </Paper>
      ))}

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={declared}
              onChange={(e) => setDeclared(e.target.checked)}
              inputRef={(el) => (fieldRefs.current.declaration = el)}
            />
          }
          label={DECLARATION}
        />
        {showErrors && !declared && (
          <FormHelperText error>Please confirm to submit</FormHelperText>
        )}
        {showErrors && completeCount < requiredFields.length && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Some answers are missing — they are highlighted above.
          </Alert>
        )}
        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}
        <Stack direction="row" justifyContent="flex-end" mt={2}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={submitting}
            startIcon={
              submitting ? <CircularProgress size={18} color="inherit" /> : null
            }
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {submitting ? 'Submitting…' : 'Submit reference'}
          </Button>
        </Stack>
      </Paper>
    </Stack>
  )

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <Header />
      <Box
        component="main"
        sx={{ flex: 1, width: '100%', maxWidth: 820, mx: 'auto', px: 2, pt: 3 }}
      >
        {renderBody()}
      </Box>
      <Footer />
    </Box>
  )
}

export default ReferenceRequest
